import { t } from "@redotech/redo-i18n";
import FlagIcon from "@redotech/redo-icons/flag-04_filled.svg";
import { StepId as ModelStepId } from "@redotech/redo-return-core-common/return-flow-answers";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const PHASE_ANCHOR_STEP = Symbol("phase_anchor_step");

export const PHASE_ORDER = ["approval", "in_transit", "delivered"] as const;
export type CompensationPhase = (typeof PHASE_ORDER)[number];

export function getPhaseLabel(phase: CompensationPhase): string {
  switch (phase) {
    case "approval":
      return t("Approval");
    case "in_transit":
      return t("In transit");
    case "delivered":
      return t("Delivered");
  }
}

/**
 * Editor-internal anchor step for one phase of the Compensation flow. Mirrors
 * how COMPENSATION_STEP / RESTOCKING_STEP work — lives in the UI layer because
 * the persisted shape is an AdvancedFlow PhaseAnchorStep. The editor uses the
 * presence of these symbols to compute swimlanes.
 */
export interface PhaseAnchorStepModel {
  type: typeof PHASE_ANCHOR_STEP;
  customTitle?: string;
  phase: CompensationPhase;
  next: ModelStepId | null;
}

export interface PhaseAnchorState {
  customTitle?: string;
  phase: CompensationPhase;
  next?: string;
}

export const PHASE_ANCHOR = (
  Details: React.ComponentType<StepTypeDetailsProps<PhaseAnchorState>>,
): StepType<PhaseAnchorState, PhaseAnchorStepModel> => ({
  Details: (props) => React.createElement(Details, props),
  get title() {
    return t("Phase");
  },
  customTitle: (state) => state.customTitle ?? getPhaseLabel(state.phase),
  description: (state) => getPhaseLabel(state.phase),
  Icon: FlagIcon,
  iconColor: "gray",
  empty: { phase: "approval" },
  downstream(state) {
    return state.next ? [{ id: state.next }] : [];
  },
  maxDownstream: () => 1,
  layout: () => BlockLayout.FULL,
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      phase: model.phase,
      next: model.next !== null ? id(model.next) : undefined,
    };
  },
  toModel(state, id) {
    return {
      type: PHASE_ANCHOR_STEP,
      customTitle: state.customTitle,
      phase: state.phase,
      next: state.next ? id(state.next) : null,
    };
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for phase anchor step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.next === stepId) {
        draft.next = undefined;
      }
    });
  },
  valid: () => true,
});
